import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GavelIcon from "@mui/icons-material/Gavel";
import HistoryIcon from "@mui/icons-material/History";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  ReportViolation,
  GetViolations,
  EscalateViolation,
  DecideApology,
  violationBody,
} from "../redux/reducer/AgreementViolation";

const HEADER_GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

const VIOLATION_REASONS = [
  "Non Payment",
  "Illegal Activities",
  "Property Damage",
  "Agreement Violation",
  "Criminal Activity",
  "Utility Bills Not Paid",
  "Other",
];

// Matches the server's `requiresEvidence` — these reports skip the tenant's
// normal notice period on escalation, so proof has to come in with the report.
// "Non Payment" is exempt: it's verified against the tenant's own rent ledger
// instead (see the server), so it needs no manual upload.
const HIGH_RISK_REASONS = ["Illegal Activities", "Criminal Activity"];
const needsEvidence = (reason, severity) =>
  reason !== "Non Payment" && (severity === "HIGH" || HIGH_RISK_REASONS.includes(reason));
const MAX_EVIDENCE_FILES = 3;
// Matches the server's NON_PAYMENT_NOTICE_DAYS.
const NON_PAYMENT_NOTICE_DAYS = 7;
const money = (n) => `Rs ${Number(n || 0).toLocaleString("en-US")}`;

const STATUS_COLOR = {
  OPEN: "warning",
  ACKNOWLEDGED: "info",
  RESOLVED: "success",
  ESCALATED: "error",
};

export default function ViolationDialog({
  open,
  onClose,
  tenantId,
  propertyId,
  tenantName,
  flatName,
}) {
  const dispatch = useDispatch();
  const { violations, loading } = useSelector(
    (state) => state.agreementViolation
  );

  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("MEDIUM");
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const evidenceRequired = needsEvidence(reason, severity);

  const handleEvidenceChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX_EVIDENCE_FILES);
    setEvidenceFiles(files);
  };

  const [escalatingId, setEscalatingId] = useState(null);
  const [escalateDueDate, setEscalateDueDate] = useState(null);
  const [escalating, setEscalating] = useState(false);

  const [decisionNote, setDecisionNote] = useState("");
  const [decidingId, setDecidingId] = useState(null);

  useEffect(() => {
    if (open && tenantId && propertyId) {
      dispatch(GetViolations({ tenantId, propertyId }));
    }
  }, [open, tenantId, propertyId, dispatch]);

  const resetForm = () => {
    setReason("");
    setDescription("");
    setSeverity("MEDIUM");
    setEvidenceFiles([]);
  };

  const handleSubmit = async () => {
    if (!reason || !description.trim()) {
      toast.warning("Please select a reason and describe the violation.");
      return;
    }
    if (evidenceRequired && evidenceFiles.length === 0) {
      toast.warning(
        "This reason requires proof — please attach at least one photo or document."
      );
      return;
    }
    setSubmitting(true);
    try {
      const res = await dispatch(
        ReportViolation({ tenantId, propertyId, reason, description, severity, evidenceFiles })
      );

      // Show whatever the API actually said, success or failure — the previous
      // generic strings hid real server errors (validation failures, auth
      // rejections) behind "Failed to log violation".
      const body = violationBody(res);

      if (body?.success) {
        toast.success(body.message || "Violation logged.");
        resetForm();
      } else {
        toast.error(body?.message || "Failed to log violation");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecision = async (violationId, decision) => {
    setDecidingId(`${violationId}-${decision}`);
    try {
      const res = await dispatch(
        DecideApology({ id: violationId, decision, note: decisionNote })
      );
      const body = violationBody(res);

      if (body?.success) {
        toast.success(
          decision === "ACCEPTED"
            ? "Apology accepted — violation resolved."
            : "Apology rejected."
        );
        setDecisionNote("");
      } else {
        toast.error(body?.message || "Failed to record decision");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setDecidingId(null);
    }
  };

  const handleEscalateConfirm = async (violationId) => {
    if (!escalateDueDate) {
      toast.warning("Please select a due date.");
      return;
    }
    setEscalating(true);
    try {
      const res = await dispatch(
        EscalateViolation({
          id: violationId,
          dueDate: escalateDueDate.format("YYYY-MM-DD"),
        })
      );
      const body = violationBody(res);

      if (body?.success) {
        const pendingReview = body?.data?.violation?.adminReview?.status === "PENDING";
        toast.success(
          body.message ||
            (pendingReview
              ? "Sent for admin review."
              : "Move-out request created and sent to tenant.")
        );
        setEscalatingId(null);
        setEscalateDueDate(null);
      } else {
        toast.error(body?.message || "Failed to escalate");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setEscalating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            background: HEADER_GRADIENT,
            color: "#fff",
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <WarningAmberIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Agreement Violations
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {tenantName ? `${tenantName} — ` : ""}
                {flatName || "This property"}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
        >
          <GavelIcon fontSize="small" color="primary" /> Report New Violation
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Reason</InputLabel>
              <Select
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                {VIOLATION_REASONS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Severity</InputLabel>
              <Select
                label="Severity"
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
              >
                <MenuItem value="LOW">Low</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              label="Describe what happened"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          {reason === "Non Payment" ? (
            <Grid item xs={12}>
              <Alert severity="info" variant="outlined">
                No upload needed — this is checked against the tenant's own
                rent record. The report only goes through if their rent is at
                least 5 days past the grace period.
              </Alert>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Button
                component="label"
                variant="outlined"
                size="small"
                color={evidenceRequired ? "error" : "primary"}
                startIcon={<AttachFileIcon fontSize="small" />}
              >
                {evidenceFiles.length > 0
                  ? `${evidenceFiles.length} file${evidenceFiles.length > 1 ? "s" : ""} attached`
                  : "Attach evidence (photo/document)"}
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*,application/pdf"
                  onChange={handleEvidenceChange}
                />
              </Button>
              {evidenceRequired && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  Required for this reason/severity — attach at least one photo
                  or document as proof.
                </Typography>
              )}
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              disabled={submitting}
              onClick={handleSubmit}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                background: HEADER_GRADIENT,
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { background: HEADER_GRADIENT, opacity: 0.9 },
              }}
            >
              {submitting ? "Logging…" : "Log Violation"}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
        >
          <HistoryIcon fontSize="small" color="primary" /> Violation History
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : violations.length === 0 ? (
          <Alert severity="info" variant="outlined">
            No violations logged for this tenant yet.
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            {violations.map((v) => (
              <Box
                key={v._id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #e5e7eb",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={v.reason} />
                    <Chip
                      size="small"
                      label={v.status}
                      color={STATUS_COLOR[v.status] || "default"}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(v.reportedAt).format("DD MMM YYYY")}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {v.description}
                </Typography>

                {v.evidence?.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                    {v.evidence.map((file, i) => (
                      <Chip
                        key={file.publicId || i}
                        size="small"
                        variant="outlined"
                        icon={<AttachFileIcon fontSize="small" />}
                        label={`Evidence ${i + 1}`}
                        component="a"
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                      />
                    ))}
                  </Stack>
                )}

                {v.systemEvidence && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.25,
                      borderRadius: 2,
                      bgcolor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      System record (rent ledger)
                    </Typography>
                    <Typography variant="body2">
                      Due {v.systemEvidence.dueDate} · {v.systemEvidence.daysOverdue} days
                      overdue · late fee {money(v.systemEvidence.penalty)} · total
                      outstanding {money(v.systemEvidence.totalOutstanding)}
                    </Typography>
                  </Box>
                )}

                {/* Tenant's response. The decision on it is the landlord's —
                    the tenant can log an apology but cannot close their own
                    violation. */}
                {v.apology?.decision === "PENDING" && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#FFF8E1",
                      border: "1px solid #FFE082",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: "#B45309" }}
                    >
                      Tenant's response
                      {v.apologyAttempts > 1 ? ` (attempt ${v.apologyAttempts})` : ""}
                      {" — awaiting your decision"}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.75, fontStyle: "italic" }}>
                      "{v.apology.message}"
                    </Typography>

                    <TextField
                      fullWidth
                      size="small"
                      sx={{ mt: 1.5, bgcolor: "#fff" }}
                      placeholder="Add a note (optional)"
                      value={decisionNote}
                      onChange={(e) => setDecisionNote(e.target.value)}
                    />

                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={Boolean(decidingId)}
                        onClick={() => handleDecision(v._id, "ACCEPTED")}
                      >
                        {decidingId === `${v._id}-ACCEPTED`
                          ? "Accepting…"
                          : "Accept apology"}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={Boolean(decidingId)}
                        onClick={() => handleDecision(v._id, "REJECTED")}
                      >
                        {decidingId === `${v._id}-REJECTED`
                          ? "Rejecting…"
                          : "Reject"}
                      </Button>
                    </Stack>
                  </Box>
                )}

                {v.apology?.decision === "ACCEPTED" && (
                  <Alert severity="success" variant="outlined" sx={{ mt: 1.5 }}>
                    You accepted the tenant's apology.
                    {v.apology.decisionNote ? ` — ${v.apology.decisionNote}` : ""}
                  </Alert>
                )}

                {v.apology?.decision === "REJECTED" && (
                  <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
                    You rejected the tenant's apology.
                    {v.apology.decisionNote ? ` — ${v.apology.decisionNote}` : ""}
                  </Alert>
                )}

                {v.adminReview?.status === "PENDING" && (
                  <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
                    Escalation submitted — awaiting admin review before a
                    move-out request is created.
                  </Alert>
                )}

                {v.adminReview?.status === "REJECTED" && (
                  <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
                    Admin declined this escalation.
                    {v.adminReview.note ? ` — ${v.adminReview.note}` : ""}
                  </Alert>
                )}

                {(v.status === "OPEN" || v.status === "ACKNOWLEDGED") &&
                  v.adminReview?.status !== "PENDING" && (
                  <Box sx={{ mt: 1.5 }}>
                    {escalatingId === v._id ? (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <DatePicker
                            label="Due Date"
                            value={escalateDueDate}
                            minDate={dayjs().add(
                              v.reason === "Non Payment" ? NON_PAYMENT_NOTICE_DAYS : 1,
                              "day"
                            )}
                            onChange={(v2) => setEscalateDueDate(v2)}
                            slotProps={{ textField: { size: "small" } }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            disabled={escalating}
                            onClick={() => handleEscalateConfirm(v._id)}
                          >
                            {escalating ? "Sending…" : "Confirm"}
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              setEscalatingId(null);
                              setEscalateDueDate(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </LocalizationProvider>
                    ) : (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => setEscalatingId(v._id)}
                      >
                        Escalate to Termination
                      </Button>
                    )}
                  </Box>
                )}

                {v.status === "ESCALATED" && (
                  <Alert severity="error" variant="outlined" sx={{ mt: 1.5 }}>
                    Move-out request sent to tenant.
                  </Alert>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
